import React, { useState } from 'react';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { FiLock, FiMail } from 'react-icons/fi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Login page — same surface palette as the rest of the dashboard.
//
// Background uses `surface-page` (the page well) with a subtle radial
// brand glow behind the card so the focal point sits in the middle
// without burning the colour into the chrome itself. The card reuses
// `card-chrome` so it reads as the same component family as everything
// inside the dashboard.

export default function LoginPage({ onLogin }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (submitting) return;
        try {
            setSubmitting(true);
            const auth = getAuth();
            await signInWithEmailAndPassword(auth, email, password);
            onLogin();
        } catch (err) {
            if (err.code === 'auth/invalid-credential') {
                toast.error('Wrong credentials');
            } else {
                toast.error(err.message);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-surface-page text-white relative overflow-hidden">
            {/* Soft brand glow — anchors the eye to the form without
                forcing colour into every surface. Radial gradient via
                a positioned div so it stays behind everything. */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        'radial-gradient(circle at 50% 30%, rgba(235,94,0,0.15), transparent 55%)',
                }}
            />

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="dark"
            />

            <form
                onSubmit={handleLogin}
                className="card-chrome w-full max-w-md p-8 shadow-2xl relative z-10"
            >
                <div className="flex flex-col items-center mb-6">
                    <img
                        src={`${process.env.PUBLIC_URL}/logo.png`}
                        alt="MUSICDJ logo"
                        className="w-14 h-14 mb-3"
                    />
                    {/* Wordmark — matches the navbar pattern: white
                        "MUSICDJ" + brand-orange "MDM". */}
                    <h1 className="text-2xl font-bold tracking-tight">
                        <span className="text-white">MUSICDJ</span>
                        <span className="text-brand"> MDM</span>
                    </h1>
                    <p className="mt-1 text-xs text-gray-500">
                        Sign in to manage your fleet
                    </p>
                </div>

                <div className="space-y-3">
                    <FieldGroup icon={<FiMail />} label="Email">
                        <input
                            type="email"
                            placeholder="you@company.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                            autoComplete="email"
                            required
                        />
                    </FieldGroup>

                    <FieldGroup icon={<FiLock />} label="Password">
                        <input
                            type="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                            autoComplete="current-password"
                            required
                        />
                    </FieldGroup>
                </div>

                <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full mt-6"
                >
                    {submitting ? 'Signing in…' : 'Sign in'}
                </button>

                <p className="text-gray-500 text-[11px] text-center mt-6 leading-relaxed">
                    This software is registered and protected.<br />
                    Copyright © MUSICDJ® LLC
                </p>
            </form>
        </div>
    );
}

// Themed field shell — a `card-inset` well with a leading icon, tiny
// uppercase label, and a transparent input on the right. Mirrors the
// HwTile/InputTile look used throughout the device detail page so the
// login form sits inside the same visual family.
function FieldGroup({ icon, label, children }) {
    return (
        <label className="card-inset px-3 py-2 flex items-center gap-3 ring-1 ring-transparent focus-within:ring-brand/40 focus-within:border-brand/40 transition-colors cursor-text">
            <span className="text-gray-500 shrink-0">{icon}</span>
            <span className="flex-1 min-w-0">
                <span className="block text-[10px] uppercase tracking-wider text-gray-500">
                    {label}
                </span>
                {children}
            </span>
        </label>
    );
}
